import { useEffect, useState, lazy, Suspense, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  PurchaseBundle,
  GetUserBundles,
  SwitchBundle,
} from "../PurchasesData.ts";
import { useProfile } from "../Context/ProfileContext";
import { useBudget } from "../Context/BudgetContext";
import {
  Container,
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Box,
  Chip,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";

// Lazy load components
const ToastContainer = lazy(() =>
  import("react-toastify").then((module) => ({
    default: module.ToastContainer,
  }))
);
const ApartmentIcon = lazy(() => import("@mui/icons-material/Apartment"));
const AppBundleWidget = lazy(() =>
  import("../sections/@dashboard/app").then((module) => ({
    default: module.AppBundleWidget,
  }))
);
const AiAdDialog = lazy(() => import("../components/AiAdDialog"));

export const BundlesPage = () => {
  const { profile, fetchProfile } = useProfile();
  const { budget, setBudget } = useBudget();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [ownedBundles, setOwnedBundles] = useState([]);
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    localStorage.setItem("isCardVisible", "true");
    localStorage.setItem("showAiAdDialog", "true");

    const fetchOwnedBundles = async () => {
      try {
        const response = await GetUserBundles();
        if (response.data) {
          setOwnedBundles(response.data);
        }
      } catch (error) {
        console.error("Error fetching owned bundles:", error);
      }
    };

    fetchOwnedBundles();
  }, []);

  const getBundleInfo = (bundleType) => {
    return ownedBundles.find(
      (b) =>
        b.bundleType &&
        b.bundleType.toLowerCase() === bundleType.toLowerCase()
    );
  };

  const handleSwitch = async (bundleType) => {
    try {
      const switchResponse = await SwitchBundle({
        UserId: profile.id,
        BundleType: bundleType,
      });

      if (switchResponse.data) {
        toast.success("Bundle switched successfully!", {
          position: "top-center",
          autoClose: 1000,
        });
        fetchProfile();
        const response = await GetUserBundles();
        if (response.data) setOwnedBundles(response.data);
      }
    } catch (error) {
      toast.error("Failed to switch bundle", {
        position: "top-center",
        autoClose: 1000,
      });
      if (error.redirectToLogin) navigate("/Login");
    }
  };

  const handlePurchase = async (ref, purchaseAmount) => {
    try {
      await startTransition(async () => {
        // Only check budget for paid bundles
        if (purchaseAmount > 0 && budget < purchaseAmount) {
          toast.error("Your budget is not enough to make this purchase!", {
            position: "top-center",
            autoClose: 2000,
          });
          return;
        }

        const response = await PurchaseBundle({
          UserId: profile.id,
          Reference: ref,
          Date: new Date(),
          Amount: purchaseAmount,
          Country: profile.profile?.country,
        });

        if (response.data) {
          if (purchaseAmount > 0) setBudget(response.data.newAmount);

          const action = response.data.action;
          const message =
            action === "renew"
              ? "Bundle renewed successfully!"
              : action === "switch"
                ? "Bundle switched successfully!"
                : "Bundle activated successfully!";

          toast.success(message, { position: "top-center", autoClose: 1000 });

          const bundlesResponse = await GetUserBundles();
          if (bundlesResponse.data) setOwnedBundles(bundlesResponse.data);
          fetchProfile();
        } else if (response.error) {
          toast.error(response.error || "Something went wrong.", {
            position: "top-center",
            autoClose: 2000,
          });
        }

        setOpenDialog(false);
      });
    } catch (error) {
      toast.error("An unexpected error occurred.", { position: "top-center" });
      if (error.redirectToLogin) navigate("/Login");
    }
  };

  const isProfessionalCurrent =
    (profile?.category || "").toLowerCase() === "professional" ||
    (profile?.category || "").toLowerCase() === "free";

  return (
    <>
      <Container>
        <Helmet>
          <title> Contactie | Bundles </title>
        </Helmet>
        <Typography variant="h5" sx={{ mb: 4 }}>
          Bundles
        </Typography>

        {/* ── HORIZONTAL PROFESSIONAL (FREE) BANNER ── */}
        <Card
          sx={{
            mb: 5,
            p: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            boxShadow: (theme) => theme.customShadows?.z8 || 3,
            borderRadius: 2,
            background: (theme) => theme.palette.background.paper,
            border: isProfessionalCurrent
              ? "2px solid rgba(255, 113, 91, 0.4)"
              : "1px solid rgba(145, 158, 171, 0.16)",
          }}
        >
          {/* Left: Label */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "220px",
              borderRight: { md: "1px solid rgba(145, 158, 171, 0.24)" },
              borderBottom: {
                xs: "1px solid rgba(145, 158, 171, 0.24)",
                md: "none",
              },
              pb: { xs: 2, md: 0 },
              pr: { md: 2 },
              mb: { xs: 2, md: 0 },
              position: "relative",
            }}
          >
            {isProfessionalCurrent && (
              <Chip
                label="Current"
                color="success"
                size="small"
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  fontWeight: 700,
                  borderRadius: 1,
                }}
              />
            )}
            <EmojiPeopleIcon color="primary" sx={{ fontSize: 60, mb: 1 }} />
            <Typography variant="h4" color="text.primary">
              Professional
            </Typography>
            <Typography variant="subtitle2" color="success.main" fontWeight={700}>
              Free (while we can) 🎉
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              Default plan for all users
            </Typography>
          </Box>

          {/* Right: Features */}
          <CardContent sx={{ flexGrow: 1, p: 0, pl: { md: 4 } }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Everything you need to get started
            </Typography>
            <Grid container spacing={1}>
              {[
                "Built-in Search Engine Discoverability",
                "Customizable Links & Social Profiles",
                "Fast Contact Info & vCard Download",
                "Resume & Pitch Deck Hosting",
                "Clients Ratings & Review System (2 Coupons/Month)",
                "AI-Powered Job Match Scoring (15/month)",
                "Performance Analytics Dashboard",
                "5 Keywords to Boost Your built-in Search Engine Rankings ",
              ].map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CheckCircleIcon
                      sx={{ color: "success.main", mr: 1, fontSize: 20 }}
                    />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* ── TWO CARDS BELOW ── */}
        <Grid container spacing={2} alignItems="stretch">

          {/* Business Card */}
          <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
            <Suspense fallback={<CircularProgress />}>
              <AppBundleWidget
                title="Business"
                amount={29}
                reference={"Business"}
                article={"Recommended"}
                icon={<ApartmentIcon fontSize="large" />}
                data={[
                  "Include All Professional features",
                  "Include +8 (Coupons/Month) for Client Ratings & Reviews",
                  "Include Next model of AI-Powered Job Match Scoring (100/month)",
                  "Include Reputation & Trust Page",
                  "Include 10 Keywords to Boost Your built-in Search Engine Rankings ",
                  "Include Priority Support",
                ]}
                sx={{ width: "100%" }}
                setOpenDialog={setOpenDialog}
                setReference={setReference}
                setAmount={setAmount}
                currentUserCategory={profile?.category}
                ownedBundles={ownedBundles}
                onSwitch={handleSwitch}
              />
            </Suspense>
          </Grid>

          {/* Silver "Coming Soon" Card */}
          <Grid item xs={12} sm={6} md={6} sx={{ display: "flex" }}>
            <Card
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                width: "100%",
                background:
                  "linear-gradient(135deg, rgba(192,192,192,0.10) 0%, rgba(169,169,169,0.04) 100%)",
                border: "1px solid rgba(192,192,192,0.28)",
                boxShadow:
                  "0 10px 24px rgba(160,160,160,0.10), 0 4px 14px rgba(0,0,0,0.06)",
                overflow: "hidden",
              }}
            >
              {/* Coming Soon Badge */}
              <Chip
                label="Coming Soon"
                size="small"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  fontWeight: 700,
                  borderRadius: 1,
                  background:
                    "linear-gradient(135deg, #b0b0b0, #888888)",
                  color: "#fff",
                }}
              />

              {/* Header */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Box
                  sx={{
                    margin: "0 auto 0 0",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "14px",
                    width: 72,
                    height: 72,
                    color: "#909090",
                    background:
                      "linear-gradient(135deg, rgba(180,180,180,0.14) 0%, rgba(140,140,140,0.08) 100%)",
                    border: "1px solid rgba(180,180,180,0.30)",
                    boxShadow: "0 10px 26px rgba(160,160,160,0.10)",
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 32, color: "#909090" }} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.2, color: "text.primary" }}>
                    Silver
                  </Typography>
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
                    <Typography
                      variant="h4"
                      sx={{ lineHeight: 1, filter: "blur(5px)", userSelect: "none" }}
                    >
                      ??
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", filter: "blur(4px)" }}>
                      Coins /month
                    </Typography>
                  </Stack>
                </Box>
              </Stack>

              {/* Blurred Features */}
              <Stack spacing={1} sx={{ mb: 2, filter: "blur(4px)", userSelect: "none", pointerEvents: "none" }}>
                {[
                  "All Business features included",
                  "+100 (Coupons/Month) for Client Ratings & Reviews",
                  "Advanced AI Job Scoring (unlimited)",
                  "Custom Domain Support",
                  "White-label Profile",
                  "Dedicated Account Manager",
                ].map((feat, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <CheckCircleIcon
                      sx={{
                        mt: "2px",
                        fontSize: 16,
                        color: "#888",
                        height: 22,
                        width: 22,
                        p: "3px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", lineHeight: 1.35 }}
                    >
                      {feat}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              {/* CTA */}
              <Box sx={{ mt: "auto" }}>
                <Button
                  fullWidth
                  disabled
                  variant="outlined"
                  size="large"
                  sx={{
                    borderRadius: 1.5,
                    py: 1.1,
                    fontSize: 15,
                    fontWeight: 700,
                    textTransform: "none",
                    borderColor: "rgba(180,180,180,0.4)",
                    color: "text.disabled",
                  }}
                >
                  Coming Soon
                </Button>
              </Box>
            </Card>
          </Grid>

        </Grid>

        <Suspense fallback={<CircularProgress />}>
          <ToastContainer />
        </Suspense>
      </Container>

      <Suspense fallback={<CircularProgress />}>
        <AiAdDialog />
      </Suspense>

      {/* Purchase / Activate Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle color="primary">
          {(() => {
            const bundleInfo = getBundleInfo(reference);
            return bundleInfo?.isExpired ? "Renew Bundle" : "Activate Bundle";
          })()}
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ padding: "20px", color: "text.secondary" }}>
            {(() => {
              const bundleInfo = getBundleInfo(reference);
              if (bundleInfo?.isExpired) {
                return amount > 0
                  ? `Your ${reference} bundle has expired. Renew it for ${amount} Coins?`
                  : `Your ${reference} bundle has expired. Reactivate it for free?`;
              }
              return amount > 0
                ? `Are you sure you want to purchase the ${reference} bundle for ${amount} Coins?`
                : `Activate the ${reference} bundle for free?`;
            })()}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => handlePurchase(reference, amount)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
